import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue, useSetAtom } from "jotai";
import React, { Suspense, useEffect, useMemo } from "react";
import { PreloadedQuery, usePreloadedQuery, useQueryLoader } from "react-relay";

import {
  DropdownSelect,
  DropdownSelectOption,
} from "@whoosh/design-system/src/components/controls/DropdownSelect";
import { Avatar } from "@whoosh/design-system/src/components/display/Avatar";
import {
  VStack,
  Text,
  HStack,
} from "@whoosh/design-system/src/components/primitive";

import { useGetClubCurrency } from "../../../../../../ClubCurrency";
import { mergeEligibleWindowCharges } from "../../../../../utils/mergeEligibleWindowCharges";
import {
  isStaffAdminAtom,
  markPrimaryChargeSelectionQueryResolvedAtom,
  mergePrimaryChargeRulePricesAtom,
  updatePrimaryChargeSelectionForSingleRegistrant,
  userTypeAtom,
} from "../../../atoms/eventSignUpProcess";
import { RegistrationChargeSelectionRowQuery } from "./__generated__/RegistrationChargeSelectionRowQuery.graphql";

export type RegistrationChargeRowPerson = {
  firstName: string | null;
  lastName: string | null;
  id: string;
  chargeEligibilityClientId: string | null;
  registrationId?: string | null;
  profileImageId?: string;
  detail?: string;
  selectedOption: string | null;
  index: number | null;
  type: "member" | "guest";
  isCurrentUser: boolean;
};

export type RegistrationChargeSelectionRowProps = {
  eventId: string;
  registrant: RegistrationChargeRowPerson;
  fallbackOptions: DropdownSelectOption<string>[];
};

export const RegistrationChargeSelectionRow = ({
  eventId,
  registrant,
  fallbackOptions,
}: RegistrationChargeSelectionRowProps) => {
  const [queryRef, loadQuery] =
    useQueryLoader<RegistrationChargeSelectionRowQuery>(
      RegistrationChargeSelectionRowQueryString
    );
  const selectedFallbackOptions = fallbackOptions.filter(
    (option) => option.value === registrant.selectedOption
  );

  useEffect(() => {
    if (registrant.chargeEligibilityClientId || registrant.registrationId) {
      loadQuery(
        {
          eventId,
          clientId: registrant.chargeEligibilityClientId,
          registrationId: registrant.registrationId ?? eventId,
          hasSavedRegistration: !!registrant.registrationId,
        },
        { fetchPolicy: "store-and-network" }
      );
    }
  }, [
    eventId,
    loadQuery,
    registrant.chargeEligibilityClientId,
    registrant.registrationId,
  ]);

  const getRegistrantName = () => {
    if (!registrant.id.length) {
      return registrant.type === "member" ? t`Member Hold` : t`Unknown Guest`;
    }
    return registrant.firstName + " " + registrant.lastName;
  };

  return (
    <VStack px={6} mb={6}>
      <HStack py={4} alignItems="center">
        <Avatar
          profileImagePublicId={registrant.profileImageId}
          firstName={registrant.firstName}
          lastName={registrant.lastName}
          type={registrant.type}
        />
        <VStack pl={4} flex={1}>
          <Text maxFontSizeMultiplier={2} fontWeight={600}>
            {getRegistrantName()}
            {registrant.isCurrentUser ? " " + t`(you)` : null}
          </Text>
          {registrant.detail ? (
            <Text
              maxFontSizeMultiplier={2}
              fontWeight={600}
              color="grayscale-800"
            >
              {registrant.detail}
            </Text>
          ) : null}
        </VStack>
      </HStack>
      <VStack minHeight={38}>
        {registrant.chargeEligibilityClientId || registrant.registrationId ? (
          <Suspense
            fallback={
              <RegistrationChargeDropdown
                registrant={registrant}
                options={selectedFallbackOptions}
              />
            }
          >
            {queryRef ? (
              <RegistrationChargeOptions
                queryRef={queryRef}
                registrant={registrant}
              />
            ) : (
              <RegistrationChargeDropdown
                registrant={registrant}
                options={selectedFallbackOptions}
              />
            )}
          </Suspense>
        ) : (
          <RegistrationChargeDropdown
            registrant={registrant}
            options={selectedFallbackOptions}
          />
        )}
      </VStack>
    </VStack>
  );
};

const RegistrationChargeOptions = ({
  queryRef,
  registrant,
}: {
  queryRef: PreloadedQuery<RegistrationChargeSelectionRowQuery>;
  registrant: RegistrationChargeRowPerson;
}) => {
  const { currencySymbol } = useGetClubCurrency();
  const data = usePreloadedQuery<RegistrationChargeSelectionRowQuery>(
    RegistrationChargeSelectionRowQueryString,
    queryRef
  );
  const isMemberUser = useAtomValue(userTypeAtom) === "MEMBER";
  const isStaffAdmin = useAtomValue(isStaffAdminAtom);
  const mergePrimaryChargeRulePrices = useSetAtom(
    mergePrimaryChargeRulePricesAtom
  );
  const markQueryResolved = useSetAtom(
    markPrimaryChargeSelectionQueryResolvedAtom
  );

  const backendSelectedWindowCharges = useMemo(() => {
    const eligibleWindows =
      data.node?.__typename === "Event"
        ? data.node.clientEligibleSignUpWindows ?? []
        : [];
    const backendSelectedWindow = eligibleWindows
      .flatMap((window) => (window ? [window] : []))
      .sort((left, right) => left.index - right.index)
      .slice(0, 1);

    return mergeEligibleWindowCharges(backendSelectedWindow);
  }, [data.node]);

  const savedCharge =
    registrant.registrationId &&
    data.registration?.id === registrant.registrationId
      ? data.registration.primaryChargeType
      : null;
  const savedPrice = savedCharge?.chargeRuleSet.chargeRules?.edges?.find(
    (edge) => edge?.node?.price != null
  )?.node?.price;

  const primaryChargeRulePrices = useMemo(
    () =>
      [
        ...backendSelectedWindowCharges.map(({ node }) => node),
        ...(savedCharge ? [savedCharge] : []),
      ].flatMap((node) => {
        const price = node.chargeRuleSet.chargeRules?.edges?.find(
          (chargeRule) => chargeRule?.node?.price != null
        )?.node?.price;

        return node.id && price != null ? [{ id: node.id, price }] : [];
      }),
    [backendSelectedWindowCharges, savedCharge]
  );

  useEffect(() => {
    mergePrimaryChargeRulePrices(primaryChargeRulePrices);
  }, [mergePrimaryChargeRulePrices, primaryChargeRulePrices]);

  useEffect(() => {
    if (registrant.chargeEligibilityClientId) {
      markQueryResolved({
        index: registrant.index,
        chargeEligibilityClientId: registrant.chargeEligibilityClientId,
      });
    }
  }, [
    markQueryResolved,
    registrant.chargeEligibilityClientId,
    registrant.index,
  ]);

  const optionsWithVisibility = backendSelectedWindowCharges.flatMap(
    ({ node, memberControl, staffControl }) => {
      const selectable = isMemberUser
        ? memberControl === "SELECTABLE"
        : staffControl !== "ADMIN_ONLY" || isStaffAdmin;
      const price = node.chargeRuleSet.chargeRules?.edges?.[0]?.node?.price;

      const isVisibleToMember = isMemberUser && memberControl === "VISIBLE";
      const shouldDisplayOption = selectable || isVisibleToMember;

      if (!shouldDisplayOption || !node.id || !node.chargeRuleSet.displayName) {
        return [];
      }

      return [
        {
          value: node.id,
          label: node.chargeRuleSet.displayName,
          isDisabled: !selectable,
          rightElement:
            price == null ? undefined : (
              <Text maxFontSizeMultiplier={2}>{`${currencySymbol}${(
                price / 100
              ).toFixed(2)}`}</Text>
            ),
        },
      ];
    }
  );

  const selectableOptions = optionsWithVisibility.filter(
    (option) => !option.isDisabled
  );
  const availableOptions =
    selectableOptions.length > 0 ? selectableOptions : optionsWithVisibility;
  const isRateLocked =
    isMemberUser &&
    !!savedCharge &&
    savedCharge.id === registrant.selectedOption &&
    !backendSelectedWindowCharges.some(
      ({ node, memberControl }) =>
        node.id === savedCharge.id && memberControl === "SELECTABLE"
    );
  const options =
    savedCharge?.id === registrant.selectedOption &&
    savedCharge?.chargeRuleSet.displayName &&
    !availableOptions.some((option) => option.value === savedCharge.id)
      ? [
          ...availableOptions,
          {
            value: savedCharge.id,
            label: savedCharge.chargeRuleSet.displayName,
            isDisabled: true,
            rightElement:
              savedPrice == null ? undefined : (
                <Text>{`$${(savedPrice / 100).toFixed(2)}`}</Text>
              ),
          },
        ]
      : availableOptions;

  const firstStaffSelectableMemberChargeId =
    backendSelectedWindowCharges.find(
      ({ node, memberControl, staffControl }) =>
        memberControl === "SELECTABLE" &&
        (staffControl !== "ADMIN_ONLY" || isStaffAdmin) &&
        !!node.id &&
        !!node.chargeRuleSet.displayName
    )?.node.id ?? null;
  const firstStaffVisibleMemberChargeId =
    backendSelectedWindowCharges.find(
      ({ node, memberControl, staffControl }) =>
        memberControl === "VISIBLE" &&
        (staffControl !== "ADMIN_ONLY" || isStaffAdmin) &&
        !!node.id &&
        !!node.chargeRuleSet.displayName
    )?.node.id ?? null;

  const selectedOptionMissing =
    registrant.selectedOption &&
    !options.some((option) => option.value === registrant.selectedOption);
  const defaultOptionId = isMemberUser
    ? options[0]?.value
    : firstStaffSelectableMemberChargeId ??
      firstStaffVisibleMemberChargeId ??
      options[0]?.value;
  const selectedOptionId = selectedOptionMissing
    ? defaultOptionId
    : registrant.selectedOption ?? defaultOptionId;

  return (
    <RegistrationChargeDropdown
      registrant={registrant}
      options={options}
      defaultOptionId={selectedOptionId}
      isDisabled={isRateLocked}
    />
  );
};

const RegistrationChargeDropdown = ({
  registrant,
  options,
  defaultOptionId,
  isDisabled = false,
}: {
  registrant: RegistrationChargeRowPerson;
  options: DropdownSelectOption<string>[];
  defaultOptionId?: string;
  isDisabled?: boolean;
}) => {
  const updatePrimaryChargeSelection = useSetAtom(
    updatePrimaryChargeSelectionForSingleRegistrant
  );

  useEffect(() => {
    if (defaultOptionId && registrant.index !== null) {
      const selectedOptionMissing =
        registrant.selectedOption &&
        !options.some((option) => option.value === registrant.selectedOption);

      if (!registrant.selectedOption || selectedOptionMissing) {
        updatePrimaryChargeSelection({
          index: registrant.index,
          primaryChargeTypeId: defaultOptionId,
        });
      }
    }
  }, [
    defaultOptionId,
    options,
    registrant.index,
    registrant.selectedOption,
    updatePrimaryChargeSelection,
  ]);

  const selectedOptionMissing =
    registrant.selectedOption &&
    !options.some((option) => option.value === registrant.selectedOption);

  return (
    <DropdownSelect
      testID={`primary-charge-${registrant.id}-${registrant.index}`}
      value={
        selectedOptionMissing
          ? defaultOptionId ?? ""
          : registrant.selectedOption ?? defaultOptionId ?? ""
      }
      options={options}
      isDisabled={isDisabled}
      onChange={(val) => {
        if (registrant.index !== null && val) {
          updatePrimaryChargeSelection({
            index: registrant.index,
            primaryChargeTypeId: val,
          });
        }
      }}
    />
  );
};

export const RegistrationChargeSelectionRowQueryString = graphql`
  query RegistrationChargeSelectionRowQuery(
    $eventId: ID!
    $clientId: ID
    $registrationId: ID!
    $hasSavedRegistration: Boolean!
  ) {
    registration: node(id: $registrationId)
      @include(if: $hasSavedRegistration) {
      ... on EventRegistration {
        id
        primaryChargeType {
          id
          chargeRuleSet {
            displayName
            chargeRules {
              edges {
                node {
                  price
                }
              }
            }
          }
        }
      }
    }
    node(id: $eventId) {
      __typename
      ... on Event {
        clientEligibleSignUpWindows(clientId: $clientId) {
          index
          charges {
            edges {
              memberControl
              staffControl
              node {
                id
                chargeRuleSet {
                  displayName
                  chargeRules {
                    edges {
                      node {
                        price
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;
